import React, { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import Avatar from "@mui/material/Avatar";
import CloseIcon from "../../../../../assets/icons/closeIcon.svg";
import plusCircle from "../../../../../assets/icons/plusCircle.svg";
import minusCircle from "../../../../../assets/icons/minusCircle.svg";
import { SearchInputComponent } from "../../../../../common/components";
import dayjs from "dayjs";
import { addKeyresult } from "../../../../../api/objectives";
import {
  getKeyResultById,
  updateKeyResult,
} from "../../../../../api/initiative";
import { formatKeyResultPayload } from "../../../company-dashboard-utils";
import "../../../company-dashboard-initiative/company-dashboard-initiative-drawer/company-dashboard-initiative-drawer-side-bar/company-dashboard-initiative-drawer-side-bar.scss";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";

const CompanyDashboardAddKeyResultsOwnerPopup: any = (props: any) => {
  const {
    isOpenAddPopup,
    onCancel,
    employeesList,
    setAlertType,
    setAlertOpen,
    setLoading,
    setAlertMessage,
    statusListKeyResults,
    objectiveId,
    keyResultId,
    fetchObjectivesById,
  } = props;
  const [KeyResultResponse, setKeyResultResponse] = useState<any>(null);
  const [selectedAddedRole] = useState<any>([]);

  const handleCreateObjectiveKeyResult = async (formValues: any) => {
    setLoading(true);
    try {
      const payload = formatKeyResultPayload(
        formValues,
        { pk: objectiveId },
        keyResultId,
        ownerIDList,
      );
      if (keyResultId) {
        await updateKeyResult(keyResultId, payload);
      } else {
        await addKeyresult(payload);
      }
      setAlertType("success");
      setAlertOpen(true);
      setLoading(false);
      onCancel();
      fetchObjectivesById();
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to create Key Result",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  const fetchKeyResultById = async () => {
    setLoading(true);
    try {
      const response = await getKeyResultById(keyResultId);
      setKeyResultResponse(response?.data?.data);
      setTimeout(() => setLoading(false), 0);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch campaigns",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (keyResultId) {
      fetchKeyResultById();
    }
  }, [keyResultId]);

  const validationSchema = () =>
    Yup.object({
      ownerId: Yup.array()
        .min(1, "At least one owner is required")
        .required("Owner is required"),
    }).shape({});
  const employeeOptions = employeesList
    ?.filter((emp: any) =>
      emp?.roles?.find((role: any) => role?.roleName === "ELT"),
    )
    ?.map((emp: any) => {
      return {
        id: emp?.employeeId,
        name: emp?.employeeFullName,
        label: emp?.employeeFullName,
        profileImage: emp?.employeeImage,
        role: "ELT",
        roleId: emp?.roles?.[0]?.roleId || null,
      };
    });

  const [Roles, setRoles] = useState(employeeOptions);
  const [ownerIDList, setOwnerIDList] = useState([]);
  const [selectedRole, setSelectedRole] = useState<any>(
    selectedAddedRole || [],
  );

  const handleSortItem = () => {
    const sortedRoles = [...selectedRole?.sort((a: any, b: any) => a.name.localeCompare(b.name)) || [], ...Roles?.filter((role: any) => !selectedRole.some((selected: any) => selected.id === role.id) && role?.role === "ELT") || []];
    setRoles(sortedRoles);
  }; 

  useEffect(() => {
    handleSortItem();
  }, [selectedRole]);




  const handleRemoveRole = (RoleText: any) => {
    setSelectedRole((prevSelected: any) =>
      prevSelected.filter((dept: any) => dept?.id !== RoleText),
    );
  };

  const handleAddRole = (RoleText: any) => {
    if (!selectedRole.some((d: any) => d.id === RoleText.id)) {
      setSelectedRole((prevSelected: any) => [...prevSelected, RoleText]);
    }
  };

  const onSearch = (value: any) => {
    if (value) {
      const filteredRoles = Roles.filter((Role: any) =>
        Role.name.toLowerCase().includes(value.toLowerCase()),
      );
      setRoles(filteredRoles);
    } else {
      setRoles(employeeOptions);
    }
  };

  useEffect(() => {
    if (keyResultId && KeyResultResponse?.owners) {
      const ownerDetails = KeyResultResponse.owners
        .map((owner: any) => {
          const emp = employeesList.find(
            (e: any) => e.employeeId === owner.employeeId,
          );
          if (!emp) return null;
          return {
            id: emp.employeeId,
            name: emp.employeeFullName,
            label: emp.employeeFullName,
            profileImage: emp.employeeImage,
            role: emp.roles?.[0]?.roleName || null,
            roleId: emp.roles?.[0]?.roleId || null,
          };
        })
        .filter(Boolean);

      setSelectedRole(ownerDetails);
      setOwnerIDList(ownerDetails.map((o: any) => o.id));
    } else {
      setSelectedRole([]);
      setOwnerIDList([]);
    }
  }, [keyResultId, KeyResultResponse, employeesList]);

  const stringAvatar = (name: string) => {
    return {
      sx: {
        bgcolor: "#403c6f",
      },
      children: `${name?.split(" ")?.[0]?.[0]}${name?.split(" ")?.[1]?.[0]}`,
    };
  };

  return (
    <Dialog
      open={isOpenAddPopup}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className={`company-dashboard-initiative-drawer-side-bar-role-popup `}
      maxWidth="sm"
      scroll="paper"
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onCancel(false);
      }}
      sx={{
        "& .MuiDialog-paper": {
          margin: "16px", // ensures it starts inside viewport
          maxHeight: "650px", // limits height to viewport
          display: "flex",
          flexDirection: "column",
        },
        "& .MuiDialog-container": {
          alignItems: "flex-start", // aligns dialog to top
        },
      }}
    >
      <DialogTitle id="alert-dialog-title">
        <div className="header-wrapper">
          <div className="header-wrapper-text">Add Owners</div>
          <div className="image-wrapper">
            <button
              style={{
                border: "none",
                background: "none",
                padding: 0,
              }}
              onClick={() => onCancel(false)}
            >
              <img
                alt="Close"
                src={CloseIcon}
                className="close-icon"
                style={{ cursor: "pointer" }}
              />
            </button>
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <Formik
            initialValues={{
              title: KeyResultResponse?.title || "",
              description: KeyResultResponse?.description || "",
              status: KeyResultResponse?.status
                ? statusListKeyResults.find(
                    (s: any) => s.statusName === KeyResultResponse?.status,
                  ) || null
                : statusListKeyResults.find(
                    (s: any) => s.statusName === "Not Started",
                  ) || null,
              startDate: KeyResultResponse?.startDate
                ? dayjs(KeyResultResponse?.startDate)
                : dayjs(),
              endDate: KeyResultResponse?.endDate
                ? dayjs(KeyResultResponse?.endDate)
                : dayjs(),
              owner: selectedRole?.length
                ? selectedRole?.map((role: any) => role.id)
                : [],
              ownerId: selectedRole?.length
                ? selectedRole?.map((role: any) => role.id)
                : [],
              weightage: KeyResultResponse?.weightage || "0",
              comments: KeyResultResponse?.comments || "",
            }}
            validationSchema={validationSchema}
            validateOnChange={true}
            enableReinitialize={true}
            onSubmit={(values) => {
              const statusId = values.status?.statusId || null;

              const newKeyResult = {
                ...values,
                statusId,
                initiatives: [],
                isSelected: true,
              };
              handleCreateObjectiveKeyResult(newKeyResult);
            }}
          >
            {({ errors }) => (
              <Form>
                <OverlayScrollbarsComponent
                  className="company-dashboard-initiative-drawer-side-bar-content-role-edit-section"
                  options={{
                    scrollbars: {
                      theme: "os-theme-dark",
                      autoHide: "scroll",
                      autoHideDelay: 400,
                    },
                  }}
                >
                  <div className="company-dashboard-initiative-drawer-side-bar-content-role-edit-search">
                    <SearchInputComponent
                      placeholder="Search by Name"
                      onSearch={onSearch}
                      isReset={false}
                      ref={null}
                    />
                  </div>
                  <div className="error-message">
                    {typeof errors?.ownerId === "string" ? (
                      <div className="error-message">{errors?.ownerId}</div>
                    ) : null}
                  </div>
                  <OverlayScrollbarsComponent
                    className="company-dashboard-initiative-drawer-side-bar-content-role-details"
                    options={{
                      scrollbars: {
                        theme: "os-theme-dark",
                        autoHide: "scroll",
                        autoHideDelay: 400,
                      },
                    }}
                  >
                    {Roles.length > 0 ? (
                      <div className="company-dashboard-initiative-drawer-side-bar-content-role-edit-roles">
                        {Roles.map((role: any, index: number) => (
                          <div
                            className= {`company-dashboard-initiative-drawer-side-bar-content-roles-edit ${selectedRole?.find((dept: any) => dept?.id === role?.id) ? "roles-edit-selected" : ""}`}
                            key={index + 1}
                          >
                            <div className="company-dashboard-initiative-drawer-side-bar-content-role-edit-content">
                              <div className="company-dashboard-initiative-drawer-side-bar-content-role-edit-content-right">
                                <div className="company-dashboard-initiative-drawer-side-bar-content-role-edit-content-right-image">
                                  {role?.profileImage ? (
                                    <img
                                      src={role?.profileImage}
                                      alt="owner"
                                      className="company-dashboard-initiative-drawer-side-bar-content-role-edit-content-right-image-img"
                                    />
                                  ) : (
                                    <Avatar {...stringAvatar(role?.name)} />
                                  )}
                                </div>
                                <div className="company-dashboard-initiative-drawer-side-bar-content-role-edit-content-right-label">
                                  {role?.name}
                                </div>
                              </div>
                              <div className="company-dashboard-initiative-drawer-side-bar-content-role-edit-content-left">
                                <div className="company-dashboard-initiative-drawer-side-bar-content-role-edit-content-left-label">
                                  {" "}
                                  {role?.role}{" "}
                                </div>
                                <div className="company-dashboard-initiative-drawer-side-bar-content-role-edit-content-left-img">
                                  {selectedRole?.find(
                                    (dept: any) => dept?.id === role?.id,
                                  ) ? (
                                    <button
                                      style={{
                                        border: "none",
                                        background: "none",
                                        padding: 0,
                                      }}
                                      type="button"
                                      onClick={() => handleRemoveRole(role.id)}
                                    >
                                      <img
                                        src={minusCircle}
                                        alt="remove"
                                        className="remove-role"
                                      />
                                    </button>
                                  ) : (
                                    <button
                                      style={{
                                        border: "none",
                                        background: "none",
                                        padding: 0,
                                      }}
                                      type="button"
                                      onClick={() => handleAddRole(role)}
                                    >
                                      <img
                                        src={plusCircle}
                                        alt="add"
                                        className="add-role"
                                      />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="no-data-found">No Employees Found</div>
                    )}
                  </OverlayScrollbarsComponent>
                  <div className="company-dashboard-initiative-drawer-side-bar-content-role-edit-action">
                    <button
                      className="company-dashboard-initiative-drawer-side-bar-content-role-edit-action-cancel"
                      onClick={() => onCancel(false)}
                      type="button"
                    >
                      Cancel
                    </button>
                    <button
                      className="company-dashboard-initiative-drawer-side-bar-content-role-edit-action-save"
                      type="submit"
                    >
                      Save
                    </button>
                  </div>
                </OverlayScrollbarsComponent>
              </Form>
            )}
          </Formik>
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
};

export default CompanyDashboardAddKeyResultsOwnerPopup;
